import { Box, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid-premium";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { useMemo, useState } from "react";

import {
  PAGE_SIZE_OPTIONS,
  SHOW_ALL_PAGE_SIZE,
  formatGridCount,
  prettyPartType,
} from "./scoutContextAnalysisFormat";
import { type PartRow } from "./useScoutConversationContextAnalysis";

export function ContextAnalysisPartsPanel({
  rows,
  loading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: {
  rows: PartRow[];
  loading: boolean;
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: PartRow) => void;
}) {
  const [showSystemParts, setShowSystemParts] = useState(false);
  const showAll = paginationModel.pageSize === SHOW_ALL_PAGE_SIZE;
  const visibleParts = useMemo(() => {
    if (showSystemParts) {
      return rows;
    }
    return rows.filter((row) => row.message.role !== "system");
  }, [rows, showSystemParts]);

  const pageOffset = showAll ? 0 : paginationModel.page * paginationModel.pageSize;
  const indexByPartId = useMemo(() => {
    const map = new Map<string, number>();
    visibleParts.forEach((row, idx) => map.set(row.id, pageOffset + idx + 1));
    return map;
  }, [visibleParts, pageOffset]);

  const columns = useMemo<CustomGridColDef<PartRow>[]>(
    () => [
      {
        field: "position",
        headerName: "#",
        width: 60,
        type: "number",
        valueGetter: (_value, row) => indexByPartId.get(row.id) ?? row.seq,
      },
      {
        field: "type",
        headerName: "Type",
        width: 140,
        valueGetter: (_value, row) => prettyPartType(row.type),
      },
      {
        field: "role",
        headerName: "Role",
        width: 90,
        valueGetter: (_value, row) => row.message.role,
      },
      {
        field: "tool",
        headerName: "Tool",
        width: 180,
        valueGetter: (_value, row) => row.toolDisplayName ?? row.toolName ?? "—",
      },
      {
        field: "preview",
        headerName: "Preview",
        flex: 1,
        minWidth: 240,
        sortable: false,
        valueGetter: (_value, row) => previewForPart(row),
      },
      {
        field: "estimatedTokens",
        headerName: "Est. size",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.estimatedTokens,
        valueFormatter: (value) => formatGridCount(value),
      },
    ],
    [indexByPartId]
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Event log of unique parts. Not billed totals; system rows are per-turn snapshots.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showSystemParts}
              onChange={(e) => setShowSystemParts(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Show system snapshots</Typography>}
          sx={{ mr: 0 }}
        />
      </Stack>
      <DataGrid<PartRow>
        rows={visibleParts}
        getRowId={(row) => row.id}
        columns={columns}
        loading={loading}
        paginationMode="server"
        pagination
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS, { value: SHOW_ALL_PAGE_SIZE, label: "Show all" }]}
        onRowClick={(params) => onRowClick(params.row)}
        disableRowSelectionOnClick
        noRowsMessage="No parts persisted on this conversation yet."
        sx={{ flex: 1, border: "none" }}
      />
    </Box>
  );
}

function previewForPart(part: PartRow): string {
  if (part.message.role === "system") {
    return "System prompt snapshot";
  }
  if (part.text) {
    return part.text;
  }
  if (part.toolDisplayName || part.toolName) {
    return `${part.toolDisplayName ?? part.toolName}${part.toolState ? ` · ${part.toolState}` : ""}`;
  }
  if (part.url) {
    return part.url;
  }
  if (part.filename) {
    return part.filename;
  }
  return "—";
}
